import {useCallback, useState} from 'react'
import PropTypes from 'prop-types'
import {Modal} from '@/components/atoms'

/**
 * Generic delete-confirmation modal.
 * Used wherever an entity needs a "Continue / Cancel" deletion flow.
 *
 * @example
 * <ConfirmDeleteModal
 *   showModal={Boolean(deletingItem)}
 *   title="Delete Route"
 *   entityName="Route"
 *   itemId={deletingItem?.id}
 *   onClose={() => setDeletingItem(null)}
 *   onConfirm={handleDelete}
 * />
 */
const ConfirmDeleteModal = ({showModal, title, entityName, itemId, onClose, onConfirm}) => {
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirm = useCallback(async () => {
    if (isDeleting) return
    setIsDeleting(true)
    const deleted = await onConfirm(itemId)
    setIsDeleting(false)
    if (deleted !== false) onClose()
  }, [isDeleting, itemId, onConfirm, onClose])

  return (
    <Modal
      showModal={showModal}
      title={title}
      isDark
      type="S"
      primaryButtonText="Continue"
      primaryButtonLoading={isDeleting}
      primaryButtonDisabled={isDeleting}
      showSecondaryButton
      secondaryButtonText="Cancel"
      onCloseModal={onClose}
      onClickSecondaryButton={onClose}
      onClickPrimaryButton={handleConfirm}
      content={
        <div data-testid="confirm-delete-modal-content" className="py-4">
          <p className="text-sm text-white">Are you sure you want to delete this {entityName}?</p>
          <p className="mt-2 text-sm text-gray-400">
            Deleting a {entityName} is a permanent action and cannot be undone.
          </p>
        </div>
      }
    />
  )
}

ConfirmDeleteModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  entityName: PropTypes.string.isRequired,
  itemId: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default ConfirmDeleteModal
